import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { partnerClientActions } from '../../actions/partner-client.actions';
import { IPartnerClient, IPartnerClientWithSecret } from '../../models/outbound/partner-client.model';
import { PartnerClientProvider } from '../../provider/partner-client.provider';

type InfoView = 'detail' | 'secret' | 'confirm_revoke';

/**
 * Card info modal: details, rotate (one-time reveal of the new secret) and
 * the two-stage revoke. Rotating invalidates the old secret immediately;
 * revoking additionally kills every outstanding token.
 */
@Component({
    selector: 'aumico-partner-client-info-modal',
    templateUrl: './partner-client-info-modal.component.html',
    styleUrls: ['./partner-client-info-modal.component.scss'],
})
export class PartnerClientInfoModalComponent implements OnDestroy {
    @Input() public client!: IPartnerClient;
    @Output() public closed = new EventEmitter<void>();

    public view: InfoView = 'detail';
    public rotated: IPartnerClientWithSecret | null = null;
    public copied = false;

    public mutating$ = this.partnerClientProvider.isMutating$();

    private readonly destroy$ = new Subject<void>();

    public constructor(
        private readonly partnerClientProvider: PartnerClientProvider,
        actions$: Actions,
    ) {
        actions$
            .pipe(ofType(partnerClientActions.rotateSecretSuccess), takeUntil(this.destroy$))
            .subscribe(({ client }) => {
                this.rotated = client;
                this.view = 'secret';
            });
        actions$
            .pipe(ofType(partnerClientActions.revokeClientSuccess), takeUntil(this.destroy$))
            .subscribe(() => this.closed.emit());
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public onRotate(): void {
        this.partnerClientProvider.rotateSecret(this.client.id);
    }

    public onRevokeRequested(): void {
        this.view = 'confirm_revoke';
    }

    public onRevokeConfirmed(): void {
        this.partnerClientProvider.revokeClient(this.client.id);
    }

    public onBackToDetail(): void {
        this.view = 'detail';
    }

    public onCopySecret(): void {
        if (this.rotated == null) {
            return;
        }
        navigator.clipboard.writeText(this.rotated.clientSecret).catch(() => undefined);
        this.copied = true;
    }

    public onClose(): void {
        this.closed.emit();
    }
}
